import React, { useEffect, useState } from "react";
import { MagnifyingGlass, CircleNotch, CaretRight, Check, Funnel, CaretDown } from "@phosphor-icons/react";
import { cn } from "@/lib/format";
import { HOME_FILTERS, FILTER_TO_MODE } from "@/lib/constants";

// SmartSearchBar — home-style search w/ filter dropdown. Used on the explorer page.
export const SmartSearchBar = ({
  value,
  onChange,
  filter,
  onFilterChange,
  onSubmit,
  loading,
  placeholder,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const inputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    if (!filterOpen) return undefined;
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [filterOpen]);

  const activeFilter = HOME_FILTERS.find((f) => f.key === filter) || HOME_FILTERS[0];

  return (
    <div className="relative z-50">
      <div className="bg-card/50 backdrop-blur-md border border-primary/20 rounded-2xl p-2 flex items-center gap-2">
        <div ref={dropdownRef} className="relative shrink-0">
          <button
            data-testid="explorer-filter-button"
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className={cn(
              "h-11 px-3.5 inline-flex items-center gap-2 mono uppercase tracking-[0.2em] text-[11px] border rounded-lg transition-colors",
              filterOpen
                ? "bg-primary/15 border-primary/50 text-primary glow-active"
                : "bg-black/40 border-primary/20 text-muted-foreground hover:text-primary hover:border-primary/40",
            )}
            aria-haspopup="listbox"
            aria-expanded={filterOpen}
          >
            <Funnel size={13} />
            <span>{activeFilter.label}</span>
            <CaretDown size={11} className={cn("transition-transform", filterOpen && "rotate-180")} />
          </button>
          {filterOpen ? (
            <div
              data-testid="explorer-filter-dropdown"
              role="listbox"
              className="absolute left-0 top-full mt-2 z-[60] min-w-[220px] bg-card/95 backdrop-blur-md border border-primary/30 rounded-lg shadow-xl shadow-primary/10 py-1.5"
            >
              {HOME_FILTERS.map((f) => {
                const isActive = f.key === filter;
                const isDisabled = FILTER_TO_MODE[f.key] === null && f.key !== "all";
                return (
                  <button
                    key={f.key}
                    data-testid={`explorer-filter-${f.key}`}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      onFilterChange?.(f.key);
                      setFilterOpen(false);
                      inputRef.current?.focus();
                    }}
                    className={cn(
                      "w-full text-left px-3 py-2 text-[11px] mono uppercase tracking-[0.2em] flex items-center justify-between gap-3 transition-colors",
                      isActive
                        ? "bg-primary/15 text-primary"
                        : "text-muted-foreground hover:bg-muted/30 hover:text-foreground",
                      isDisabled && !isActive && "opacity-60",
                    )}
                  >
                    <span className="inline-flex items-center gap-2">
                      {isActive ? <Check size={11} /> : <span className="w-3" />}
                      {f.label}
                    </span>
                    {isDisabled ? (
                      <span className="text-[9px] text-muted-foreground/70">soon</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        <span className="h-7 w-px bg-primary/15 shrink-0" aria-hidden="true" />
        <MagnifyingGlass size={18} className="text-primary/70 shrink-0" />
        <input
          ref={inputRef}
          data-testid="search-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onSubmit(); }}
          placeholder={placeholder}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="none"
          className="flex-1 bg-transparent outline-none py-3 text-[14px] mono text-foreground placeholder:text-muted-foreground/60"
        />
        <button
          data-testid="search-submit"
          type="button"
          onClick={onSubmit}
          disabled={loading || !value.trim()}
          className="glow-cta px-6 py-3 bg-primary text-primary-foreground font-bold tracking-[0.2em] uppercase text-[11px] rounded-lg disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
        >
          {loading ? <CircleNotch size={12} className="animate-spin" /> : null}
          search <CaretRight size={12} />
        </button>
      </div>
    </div>
  );
};
